import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { MessageCircle } from 'lucide-react';
import { getDeliveryHubSchools, DeliveryHubSchool, getChatUnreadCounts } from '@/actions/delivery-hub';
import { socketService } from '@/services/socketService';
import { playNotificationSound, showBrowserNotification, requestNotificationPermission } from '@/services/chatNotification';

interface DeliveryHubContextType {
  schools: DeliveryHubSchool[];
  loading: boolean;
  chatUnreadCounts: Record<number, number>;
  getSchoolById: (id: string | number) => DeliveryHubSchool | undefined;
  refreshSchools: () => Promise<void>;
  updateSchoolCounts: (schoolId: number, delta: { revision?: number; query?: number }) => void;
  refreshChatUnread: () => Promise<void>;
  setActiveChatSlateId: (id: number | null) => void;
}

const DeliveryHubContext = createContext<DeliveryHubContextType | null>(null);

export const DeliveryHubProvider = ({ children }: { children: ReactNode }) => {
  const [schools, setSchools] = useState<DeliveryHubSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatUnreadCounts, setChatUnreadCounts] = useState<Record<number, number>>({});
  const hasFetched = useRef(false);
  // Refs for stable access inside socket handlers (avoids stale closure)
  const schoolsRef = useRef<DeliveryHubSchool[]>([]);
  const activeChatSlateIdRef = useRef<number | null>(null);

  // Keep schoolsRef in sync
  useEffect(() => { schoolsRef.current = schools; }, [schools]);

  const setActiveChatSlateId = useCallback((id: number | null) => {
    activeChatSlateIdRef.current = id;
  }, []);

  const fetchSchools = async () => {
    setLoading(true);
    const data = await getDeliveryHubSchools();
    setSchools(data);
    setLoading(false);
  };

  const refreshChatUnread = useCallback(async () => {
    const counts = await getChatUnreadCounts();
    setChatUnreadCounts(counts);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchSchools();
    refreshChatUnread();
    requestNotificationPermission();
  }, []);

  // Unread counts resync every 20s; when the socket is down this also
  // shows toast + sound when a school's unread count increases
  useEffect(() => {
    const prevCountsRef = { current: {} as Record<number, number> };

    const poll = async () => {
      // Always resync with the server (reads in other tabs, missed socket events);
      // toast/sound only when the socket isn't delivering them in real time
      const socketLive = socketService.isConnected();
      const newCounts = await getChatUnreadCounts();
      setChatUnreadCounts((prev) => {
        if (socketLive) return newCounts;
        Object.entries(newCounts).forEach(([idStr, count]) => {
          const slateId = Number(idStr);
          const prevCount = prev[slateId] || 0;
          if (count > prevCount && activeChatSlateIdRef.current !== slateId) {
            const school = schoolsRef.current.find((s) => s.slate_id === slateId);
            const schoolName = school?.slate_name?.replace(/_/g, ' ') || `School ${slateId}`;
            playNotificationSound();
            showBrowserNotification(schoolName, 'New message received');
            toast.info(
              <div className="flex items-start gap-2">
                <MessageCircle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-sm">{schoolName}</div>
                  <div className="text-xs text-gray-600 mt-0.5">New message received</div>
                </div>
              </div>,
              { autoClose: 6000 }
            );
          }
        });
        prevCountsRef.current = newCounts;
        return newCounts;
      });
    };

    const timer = setInterval(poll, 20_000); // every 20s
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Global listener: join admin_notifications room and show toast when a client sends a message
  useEffect(() => {
    const joinAdminRoom = () => {
      socketService.emit('join_admin_notifications');
    };

    // Join on the socket.io 'connect' event — fires on every connect + reconnect
    socketService.on('connect', joinAdminRoom);
    // Also try immediately in case the socket is already connected
    joinAdminRoom();

    const handleClientMessage = ({ slateId, message }: { slateId: number; message: any }) => {
      // Admin is viewing that school's chat: the open window marks it read, so no badge or toast
      if (activeChatSlateIdRef.current === slateId) return;

      setChatUnreadCounts((prev) => ({
        ...prev,
        [slateId]: (prev[slateId] || 0) + 1,
      }));

      const school = schoolsRef.current.find((s) => s.slate_id === slateId);
      const schoolName = school?.slate_name?.replace(/_/g, ' ') || `School ${slateId}`;
      const preview = message?.message
        ? message.message.length > 60
          ? message.message.slice(0, 60) + '…'
          : message.message
        : 'New message';

      // Sound + OS notification
      playNotificationSound();
      showBrowserNotification(schoolName, preview);

      // In-app toast
      toast.info(
        <div className="flex items-start gap-2">
          <MessageCircle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-sm">{schoolName}</div>
            <div className="text-xs text-gray-600 mt-0.5">{preview}</div>
          </div>
        </div>,
        { autoClose: 6000 }
      );
    };

    socketService.on('new_client_chat_message', handleClientMessage);

    return () => {
      socketService.off('connect', joinAdminRoom);
      socketService.off('new_client_chat_message', handleClientMessage);
    };
  }, []);

  const getSchoolById = (id: string | number) => {
    return schools.find(s => String(s.slate_id) === String(id));
  };

  const refreshSchools = async () => {
    await fetchSchools();
  };

  // Update counts locally without refetching (for optimistic updates)
  const updateSchoolCounts = (schoolId: number, delta: { revision?: number; query?: number }) => {
    setSchools((prev) =>
      prev.map((school) => {
        if (school.slate_id === schoolId) {
          const newRevisionCount = (school.revision_count || 0) + (delta.revision || 0);
          const newQueryCount = (school.query_count || 0) + (delta.query || 0);
          return {
            ...school,
            revision_count: Math.max(0, newRevisionCount),
            query_count: Math.max(0, newQueryCount),
            attention_count: Math.max(0, newRevisionCount) + Math.max(0, newQueryCount),
          };
        }
        return school;
      })
    );
  };

  return (
    <DeliveryHubContext.Provider value={{ schools, loading, chatUnreadCounts, getSchoolById, refreshSchools, updateSchoolCounts, refreshChatUnread, setActiveChatSlateId }}>
      {children}
    </DeliveryHubContext.Provider>
  );
};

export const useDeliveryHub = () => {
  const context = useContext(DeliveryHubContext);
  if (!context) {
    throw new Error('useDeliveryHub must be used within DeliveryHubProvider');
  }
  return context;
};
